import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Heart, Share2, Tag } from 'react-feather';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Spinner } from 'components';
import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';
import { actions as appliedCouponsActions } from 'coupon-common/src/modules/customerCoupons/applied';
import { actions as applyActions } from 'coupon-common/src/modules/customerCoupons/apply';
import { actions as reserveActions } from 'coupon-common/src/modules/customerCoupons/reserve';
import { actions as customerProfileActions } from 'coupon-common/src/modules/customerProfile';

import { RouteNames } from '../../../constants';
import styles from './CouponsDetails.module.scss';

const DO_NOT_SHOW_KEY = 'doNotShowReserveModal';

/**
 * Favourite toggle + reserve/redeem action row (inline in the hero card, per
 * the redesign) + confirmation modals for a coupon.
 *
 * UPDATE: reserve/apply now go through real redux actions
 * (customerCoupons/reserve, customerCoupons/apply — ported from the mobile
 * coupons/reserve + coupons/apply ducks) instead of calling the saga
 * generator functions from coupons/api directly. Those API functions are
 * thin wrappers around `authApiCall`, a redux-saga generator — calling them
 * with `await` outside saga middleware just returns an unstarted generator
 * object, never makes the request, and resolves near-instantly with
 * `undefined` data. That was the root cause of reserve/apply silently
 * "succeeding" with no real effect.
 *
 * Both reserveCoupon and applyCoupon action creators take (couponId,
 * payByPoints, resolve, reject) and call resolve/reject directly as plain
 * functions from inside the saga (not via dispatchAsAsync) — so we wrap
 * each dispatch in a Promise here to keep using async/await in this
 * component.
 *
 * REDEEM BUTTON COLOR: while a reservation is active (isReserved() true and
 * not 'waiting'), the redeem button uses styles.reserved instead of
 * styles.yellow — .reserved applies linear-gradient(90deg, #ef4444,
 * #7f1d1d) per the CouponsDetails redesign, matching heroDealAmount's own
 * gradient. This stays in effect for as long as isReserved() is true, i.e.
 * up until the reservation's expiry (backed by customerCoupons.applied,
 * same source getCouponState() already reads from).
 *
 * SHARE + FAVOURITE: the old pill-shaped, text-and-icon Favorite button
 * (.favBtn) has been replaced with a pair of circle icon-only buttons
 * (.iconBtn) — Share on the left, Favourite (heart) on the right — rendered
 * via `actionIcons` below, used in both the affiliate and standard action
 * rows. Share uses the native Web Share API where available (mobile) and
 * falls back to copying the coupon URL to the clipboard with a small toast
 * on desktop.
 *
 * LOGIN GATE (new): the login-prompt modal that already gated the primary
 * Activate button (handleButtonPress) is now shared by the favourite
 * button too — handleFavouriteClick() checks the same `authorized` flag
 * before calling onToggleFavourite(), and opens the same
 * isLoginPromptVisible modal on a miss. The modal copy was generic ("to
 * continue") to begin with, so no per-action text branching was needed.
 *
 * GUEST FAVOURITE BUTTON: previously `disabled={!favouritesFetched}` — for
 * a guest, the favourites list fetch 401s and never resolves `fetched`,
 * so the button was permanently disabled and a guest could never trigger
 * the login prompt by clicking it. Now only disabled while *authorized*
 * and still loading; a guest's un-fetched favourites list no longer blocks
 * the click.
 */
const CouponAction = ({
  data,
  dispatch,
  gotoGetDirection,
  locked,
  paywalled,
  isAffiliate = false,
  affiliateLink = '',
  isFavourite = false,
  favouritesFetched = false,
  onToggleFavourite = () => {},
}) => {
  const navigate = useNavigate();

  const [isModalVisible, setModalVisible] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(
    () => localStorage.getItem(DO_NOT_SHOW_KEY) === 'true',
  );
  const [limitModalVisible, setLimitModalVisible] = useState(false);
  const [isLoginPromptVisible, setIsLoginPromptVisible] = useState(false);
  const [isReserving, setIsReserving] = useState(false);
  const [isLocallyReserved, setIsLocallyReserved] = useState(false);
  const [reserveError, setReserveError] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);

  const customerProfile = useSelector(state => state.customerProfile);
  const appliedCoupons = useSelector(state => state.customerCoupons.applied);

  // Guests can't have applied/reserved coupons (ApplyCouponViewSet is
  // IsCustomer-only, correctly), so fetching this for them always 403s.
  // If the applied duck's reducer only sets `fetched: true` on success
  // (typical), a guest's appliedCoupons.fetched would never flip true and
  // the Reserve button would show "Loading..." forever. Treat "guest" as
  // an already-resolved, empty applied-coupons state instead of a
  // pending fetch — `appliedFetched` below is what the button actually
  // reads, not the raw redux flag.
  const authorized = useSelector(state => !!(state.auth && state.auth.authorized));
  const [appliedGaveUp, setAppliedGaveUp] = useState(false);
const appliedAttemptsRef = useRef(0);
const appliedFetched = authorized ? !!appliedCoupons?.fetched || appliedGaveUp : true;

  console.log('CouponAction render', {
    couponId: data?.id,
    authorized,
    appliedFetched,
  });

  // Tracks whether this component is still mounted, so the async
  // handlers below (handleConfirm/handleRedeem) don't call setState after
  // navigate() has already unmounted it. navigate() fires synchronously
  // inside the try blocks, but the surrounding finally/catch still runs
  // afterward on the same tick — this guards just those calls.
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getAppliedCoupons = useCallback(() => {
    dispatch(appliedCouponsActions.getApplyCouponList());
  }, [dispatch]);

useEffect(() => {
  if (!authorized) return;
  dispatch(customerProfileActions.getCustomerProfile());
}, [dispatch, authorized]);

// Fetch the applied list if it isn't loaded yet; retry once on failure,
// then stop so the button never sits on "Loading..." forever.
useEffect(() => {
  if (!authorized || appliedCoupons?.fetched || appliedCoupons?.fetching) return;
  if (appliedAttemptsRef.current >= 2) {
    setAppliedGaveUp(true);
    return;
  }
  appliedAttemptsRef.current += 1;
  getAppliedCoupons();
}, [authorized, appliedCoupons?.fetched, appliedCoupons?.fetching, getAppliedCoupons]);

  const couponCount = appliedCoupons?.result?.results?.length || 0;
  const limitReached =
    !customerProfile?.data?.has_active_subscription &&
    !!customerProfile?.data?.coupon_limit &&
    couponCount >= customerProfile.data.coupon_limit;

  const getCouponState = couponId => {
    const items = appliedCoupons?.items || {};
    const match = Object.values(items).find(c => c.coupon === couponId);
    return match?.state || null;
  };

  const isReserved = () => {
    const items = appliedCoupons?.items || {};
    return Object.values(items).some(
      c => c.coupon === data?.id && (c.state === 'reserved' || c.state === 'waiting'),
    );
  };

  // Promise wrappers around the reserve/apply actions, since their sagas
  // call resolve/reject as plain functions rather than going through
  // dispatchAsAsync.
  const dispatchReserveCoupon = (couponId, payByPoints = false) =>
    new Promise((resolve, reject) => {
      dispatch(reserveActions.reserveCoupon(couponId, payByPoints, resolve, reject));
    });

  const dispatchApplyCoupon = couponId =>
    new Promise((resolve, reject) => {
      // applyCoupon's saga doesn't currently call resolve/reject itself (it
      // only writes to redux state), so resolve immediately after dispatch —
      // the component falls back to redux state (applyCouponState below) for
      // the actual result rather than awaiting this promise's value.
      dispatch(applyActions.applyCoupon(couponId, false));
      resolve();
    });

  const dispatchGetApplyCoupon = appliedCouponId =>
    new Promise((resolve, reject) => {
      dispatch(applyActions.getApplyCoupon(appliedCouponId, resolve, reject));
    });

  const handleButtonPress = () => {
    if (!authorized) {
      setIsLoginPromptVisible(true);
      return;
    }

    if (locked) {
      // eslint-disable-next-line no-alert
      const shouldFollow = window.confirm(
        'This offer is only for followers! Follow this business to access this deal and get notified about their latest coupons.\n\nFollow now?',
      );
      if (shouldFollow && data?.business?.id) {
        dispatch(followActions.followBusinessRequest({ business_id: data.business.id }));
      }
      return;
    }

    if (paywalled) {
      // eslint-disable-next-line no-alert
      const shouldUpgrade = window.confirm(
        'This offer is only for Premium Users! Subscribe to access this and other premium offers.\n\nUpgrade now?',
      );
      if (shouldUpgrade) {
        navigate(RouteNames.ACCOUNT);
      }
      return;
    }

    if (limitReached) {
      setLimitModalVisible(true);
    } else if (doNotShowAgain) {
      handleConfirm();
    } else {
      setModalVisible(true);
    }
  };

  const handleConfirm = async () => {
    if (doNotShowAgain) {
      localStorage.setItem(DO_NOT_SHOW_KEY, 'true');
    }
    setModalVisible(false);

    if (limitReached) {
      setLimitModalVisible(true);
      return;
    }

    setIsReserving(true);
    setReserveError(null);
    try {
      const responseData = await dispatchReserveCoupon(data.id);
      // Navigate first, same reasoning as handleRedeem below — this
      // component unmounts on navigate, and ReservedCoupon reads only from
      // location.state, not customerCoupons.applied.
      navigate(RouteNames.COUPONS_RESERVED, { state: { reservedData: responseData ?? null } });
    } catch (err) {
      console.error('Error reserving coupon:', err);
      if (isMountedRef.current) {
        setReserveError((typeof err === 'string' ? err : err?.message) || 'Something went wrong reserving this coupon.');
      }
    } finally {
      if (isMountedRef.current) {
        setIsReserving(false);
      }
    }
  };

  const handleRedeem = async () => {
    const state = getCouponState(data.id);
    if (state === 'waiting') {
      // eslint-disable-next-line no-alert
      window.alert('This coupon is waiting for business approval.');
      return;
    }

    try {
      if (data?.applied_coupon_id) {
        const responseData = await dispatchGetApplyCoupon(data.applied_coupon_id);
        navigate(RouteNames.COUPONS_RESERVED, { state: { reservedData: responseData ?? null } });
      } else {
        await dispatchApplyCoupon(data.id);
        // Navigate first — this component unmounts immediately after, and
        // the next screen (ReservedCoupon) reads entirely from
        // location.state, not from customerCoupons.applied. Dispatching
        // getAppliedCoupons() *before* navigate (as this used to) left a
        // window where its saga could resolve after unmount and trigger a
        // "state update on an unmounted component" warning via this
        // component's own useSelector subscription. Any other screen that
        // needs a fresh applied list re-fetches it themselves on mount.
        navigate(RouteNames.COUPONS_RESERVED, { state: { reservedData: data ?? null } });
      }
    } catch (err) {
      console.error('Error applying coupon:', err);
      if (isMountedRef.current) {
        setReserveError((typeof err === 'string' ? err : err?.message) || 'Redemption failed.');
      }
    }
  };

  // ─── Favourite (login-gated, same modal as Activate) ──────────────────
  const handleFavouriteClick = () => {
    if (!authorized) {
      setIsLoginPromptVisible(true);
      return;
    }
    onToggleFavourite();
  };

  // ─── Share ──────────────────────────────────────────────────────────────
  const handleShare = useCallback(async () => {
    if (!data?.id) return;
    const shareUrl = `${window.location.origin}${RouteNames.COUPONS_DETAILS}/${data.id}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: data?.title || 'Coupon',
          text: `Check out this deal: ${data?.title || ''}`,
          url: shareUrl,
        });
      } catch (err) {
        // user dismissed the native share sheet — no-op
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.prompt('Copy this link:', shareUrl);
    }
  }, [data?.id, data?.title]);

  // Circular icon-only Share + Favourite buttons, rendered inline before
  // the primary action button in .actionRow (both the affiliate and
  // standard variants below).
  const actionIcons = (
    <div className={styles.iconBtnRow}>
      <div className={styles.iconBtnWrap}>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={handleShare}
          aria-label="Share this coupon"
        >
          <Share2 size={18} />
        </button>
        {shareCopied && <span className={styles.shareToast}>Link copied!</span>}
      </div>

      <button
        type="button"
        className={isFavourite ? `${styles.iconBtn} ${styles.active}` : styles.iconBtn}
        onClick={handleFavouriteClick}
        disabled={authorized && !favouritesFetched}
        aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
      >
        <Heart size={18} fill={isFavourite ? 'currentColor' : 'none'} />
      </button>
    </div>
  );

  if (isAffiliate) {
    return (
      <div>
        <div className={styles.actionRow}>
          {actionIcons}
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={() => {
              if (affiliateLink) {
                const withScheme = /^https?:\/\//i.test(affiliateLink) ? affiliateLink : `https://${affiliateLink}`;
                window.open(withScheme, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            <Tag size={16} />
            View Deal
          </button>
        </div>
        <p className={styles.affiliateDisclosure}>
          My Couponbook may earn a commission when you buy through links on our site.
        </p>
      </div>
    );
  }

  return (
    <>
      {isModalVisible && (
        <div className={styles.modalOverlay} onClick={() => setModalVisible(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Reserve Now?</h2>
            <p className={styles.modalMessage}>
              This coupon will be reserved for <strong>{data?.reserved_hours} Hours</strong>. If you
              don&apos;t use it in time you can reserve it again if it&apos;s still available.
            </p>
            {!customerProfile?.data?.has_active_subscription && customerProfile?.data?.coupon_limit && (
              <p className={styles.modalMessage}>
                On the free plan, you have{' '}
                <strong>{customerProfile.data.coupon_limit - couponCount}</strong> remaining coupon
                reservations this month.
              </p>
            )}
            {customerProfile?.data?.has_active_subscription && (
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={doNotShowAgain}
                  onChange={e => setDoNotShowAgain(e.target.checked)}
                />
                Do not show this message again
              </label>
            )}
            <div className={styles.modalButtons}>
              <button type="button" className={styles.modalBtnNo} onClick={() => setModalVisible(false)}>
                No
              </button>
              <button type="button" className={styles.modalBtnYes} onClick={handleConfirm}>
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {isReserving && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.spinnerRow}>
              <Spinner show size="md" />
            </div>
            <p className={styles.modalMessage}>Reserving your coupon...</p>
          </div>
        </div>
      )}

      {limitModalVisible && (
        <div className={styles.modalOverlay} onClick={() => setLimitModalVisible(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Coupon Reservation Limit Reached</h2>
            <p className={styles.modalMessage}>
              You&apos;ve reached your monthly coupon reservation limit. Upgrade your plan to reserve
              coupons without restrictions.
            </p>
            <div className={styles.modalButtons}>
              <button type="button" className={styles.modalBtnNo} onClick={() => setLimitModalVisible(false)}>
                Later
              </button>
              <button
                type="button"
                className={`${styles.modalBtnYes} ${styles.yellow}`}
                onClick={() => {
                  setLimitModalVisible(false);
                  navigate(RouteNames.ACCOUNT);
                }}
              >
                Upgrade
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoginPromptVisible && (
        <div className={styles.modalOverlay} onClick={() => setIsLoginPromptVisible(false)}>
          <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Log in to continue</h2>
            <p className={styles.modalMessage}>
              Create a free account (or log in) to save favourites and reserve coupons.
            </p>
            <div className={styles.modalButtons}>
              <button
                type="button"
                className={styles.modalBtnYes}
                onClick={() => {
                  setIsLoginPromptVisible(false);
                  navigate(RouteNames.SIGN_IN);
                }}
              >
                Login
              </button>
            </div>
          </div>
        </div>
      )}

      {reserveError && <p className={styles.stateText}>{reserveError}</p>}

      <div className={styles.actionRow}>
        {actionIcons}
        {isLocallyReserved || isReserved() ? (
          getCouponState(data?.id) === 'waiting' ? (
            <button type="button" className={`${styles.primaryBtn} ${styles.yellow}`} disabled style={{ opacity: 0.7 }}>
              Waiting for acceptance
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.primaryBtn} ${styles.reserved}`}
              onClick={handleRedeem}
              disabled={!appliedFetched}
            >
              <Tag size={16} />
              {!appliedFetched ? 'Loading...' : 'Redeem'}
            </button>
          )
        ) : (
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleButtonPress}
            disabled={!appliedFetched}
          >
            <Tag size={16} />
            {!appliedFetched ? 'Loading...' : 'Activate'}
          </button>
        )}
      </div>
    </>
  );
};

export default CouponAction;